// Artifact content viewers: the chrome words inside a document, report or
// spreadsheet that are not the artifact's own content (T1-UI1). Namespace
// `artifact-content`.
//
// A missing, redacted or null value, and a reference to another artifact, are
// states of the viewer, so they are copy here. The values themselves, titles and
// narration come from the artifact. All 24 app languages are written out below: `en` and `sv` are the source, the rest
// are translated chrome copy (placeholders and the generic word "Artifact" preserved).
// See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  valueMissing: 'Not available',
  valueRedacted: 'Hidden',
  valueSourceNull: 'No value in the source',
  valuePending: 'Still being written',
  tablePlaceholder: 'Table',
  tableDescribed: 'Table: {title}',
  tableRows: '{count} rows',
  openInSheet: 'Open in sheet',
  chartPlaceholder: 'Chart',
  chartDescribed: 'Chart: {title}',
  openChart: 'Open',
  imageUnavailable: 'Image not available',
  pageLabel: 'Page {at} of {count}',
  documentLabel: 'Document',
  reportLabel: 'Report',
  sheetTabs: 'Sheets',
  sheetGrid: 'Sheet {title}',
  formulaBar: 'Formula',
  formulaBarEmpty: 'Select a cell to see how its value is made.',
  fromSources: 'From {count} sources',
  fromSource: 'From 1 source',
  typedValue: 'Entered value',
  errorCycle: 'Circular reference',
  errorUnsupported: 'Not supported',
  errorMissingInput: 'An input is missing',
  errorDivisionByZero: 'Division by zero',
  errorTypeError: 'Wrong kind of value',
  errorGeneric: 'Error',
  runtimeOpening: 'Opening…',
  runtimeInvalidated: 'This view was closed.',
  runtimeFailed: 'This view could not be opened.',
  runtimeReload: 'Open it again',
  runtimeDetails: 'Details',
  sourcesTitle: 'Sources',
  sourcesValue: 'Value',
  sourcesSource: 'Source',
  sourcesAsOf: 'As of',
  slideNumber: '{at} / {count}',
};

const sv: typeof en = {
  valueMissing: 'Saknas',
  valueRedacted: 'Dolt',
  valueSourceNull: 'Inget värde i källan',
  valuePending: 'Skrivs fortfarande',
  tablePlaceholder: 'Tabell',
  tableDescribed: 'Tabell: {title}',
  tableRows: '{count} rader',
  openInSheet: 'Öppna i kalkylblad',
  chartPlaceholder: 'Diagram',
  chartDescribed: 'Diagram: {title}',
  openChart: 'Öppna',
  imageUnavailable: 'Bilden är inte tillgänglig',
  pageLabel: 'Sida {at} av {count}',
  documentLabel: 'Dokument',
  reportLabel: 'Rapport',
  sheetTabs: 'Blad',
  sheetGrid: 'Bladet {title}',
  formulaBar: 'Formel',
  formulaBarEmpty: 'Välj en cell för att se hur värdet är gjort.',
  fromSources: 'Från {count} källor',
  fromSource: 'Från 1 källa',
  typedValue: 'Inmatat värde',
  errorCycle: 'Cirkelreferens',
  errorUnsupported: 'Stöds inte',
  errorMissingInput: 'Ett indata saknas',
  errorDivisionByZero: 'Division med noll',
  errorTypeError: 'Fel sorts värde',
  errorGeneric: 'Fel',
  runtimeOpening: 'Öppnar…',
  runtimeInvalidated: 'Den här vyn stängdes.',
  runtimeFailed: 'Den här vyn kunde inte öppnas.',
  runtimeReload: 'Öppna igen',
  runtimeDetails: 'Detaljer',
  sourcesTitle: 'Källor',
  sourcesValue: 'Värde',
  sourcesSource: 'Källa',
  sourcesAsOf: 'Per datum',
  slideNumber: '{at} / {count}',
};

type Key = keyof typeof en;

export type ArtifactContentCopy = Readonly<Record<Key, string>>;

const translated: Omit<NamespaceCatalog<Key>, 'en' | 'sv'> = {
  // Bulgarian
  bg: {
    valueMissing: 'Не е налично',
    valueRedacted: 'Скрито',
    valueSourceNull: 'Няма стойност в източника',
    valuePending: 'Още се пише',
    tablePlaceholder: 'Таблица',
    tableDescribed: 'Таблица: {title}',
    tableRows: 'Редове: {count}',
    openInSheet: 'Отваряне в листа',
    chartPlaceholder: 'Диаграма',
    chartDescribed: 'Диаграма: {title}',
    openChart: 'Отваряне',
    imageUnavailable: 'Изображението не е налично',
    pageLabel: 'Страница {at} от {count}',
    documentLabel: 'Документ',
    reportLabel: 'Отчет',
    sheetTabs: 'Листове',
    sheetGrid: 'Лист {title}',
    formulaBar: 'Формула',
    formulaBarEmpty: 'Изберете клетка, за да видите как е получена стойността ѝ.',
    fromSources: 'От {count} източника',
    fromSource: 'От 1 източник',
    typedValue: 'Въведена стойност',
    errorCycle: 'Циклична препратка',
    errorUnsupported: 'Не се поддържа',
    errorMissingInput: 'Липсва входна стойност',
    errorDivisionByZero: 'Деление на нула',
    errorTypeError: 'Грешен вид стойност',
    errorGeneric: 'Грешка',
    runtimeOpening: 'Отваряне…',
    runtimeInvalidated: 'Този изглед беше затворен.',
    runtimeFailed: 'Този изглед не можа да бъде отворен.',
    runtimeReload: 'Отваряне отново',
    sourcesTitle: 'Източници',
    sourcesValue: 'Стойност',
    sourcesSource: 'Източник',
    sourcesAsOf: 'Към',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Подробности',
  },
  // Czech
  cs: {
    valueMissing: 'Není k dispozici',
    valueRedacted: 'Skryto',
    valueSourceNull: 'Ve zdroji není hodnota',
    valuePending: 'Stále se píše',
    tablePlaceholder: 'Tabulka',
    tableDescribed: 'Tabulka: {title}',
    tableRows: 'Řádků: {count}',
    openInSheet: 'Otevřít v listu',
    chartPlaceholder: 'Graf',
    chartDescribed: 'Graf: {title}',
    openChart: 'Otevřít',
    imageUnavailable: 'Obrázek není k dispozici',
    pageLabel: 'Strana {at} z {count}',
    documentLabel: 'Dokument',
    reportLabel: 'Zpráva',
    sheetTabs: 'Listy',
    sheetGrid: 'List {title}',
    formulaBar: 'Vzorec',
    formulaBarEmpty: 'Vyberte buňku a uvidíte, jak vznikla její hodnota.',
    fromSources: 'Z {count} zdrojů',
    fromSource: 'Z 1 zdroje',
    typedValue: 'Zadaná hodnota',
    errorCycle: 'Cyklický odkaz',
    errorUnsupported: 'Nepodporováno',
    errorMissingInput: 'Chybí vstup',
    errorDivisionByZero: 'Dělení nulou',
    errorTypeError: 'Nesprávný druh hodnoty',
    errorGeneric: 'Chyba',
    runtimeOpening: 'Otevírá se…',
    runtimeInvalidated: 'Toto zobrazení bylo zavřeno.',
    runtimeFailed: 'Toto zobrazení se nepodařilo otevřít.',
    runtimeReload: 'Otevřít znovu',
    sourcesTitle: 'Zdroje',
    sourcesValue: 'Hodnota',
    sourcesSource: 'Zdroj',
    sourcesAsOf: 'K datu',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Podrobnosti',
  },
  // Danish
  da: {
    valueMissing: 'Ikke tilgængelig',
    valueRedacted: 'Skjult',
    valueSourceNull: 'Ingen værdi i kilden',
    valuePending: 'Er stadig ved at blive skrevet',
    tablePlaceholder: 'Tabel',
    tableDescribed: 'Tabel: {title}',
    tableRows: 'Rækker: {count}',
    openInSheet: 'Åbn i arket',
    chartPlaceholder: 'Diagram',
    chartDescribed: 'Diagram: {title}',
    openChart: 'Åbn',
    imageUnavailable: 'Billedet er ikke tilgængeligt',
    pageLabel: 'Side {at} af {count}',
    documentLabel: 'Dokument',
    reportLabel: 'Rapport',
    sheetTabs: 'Ark',
    sheetGrid: 'Ark {title}',
    formulaBar: 'Formel',
    formulaBarEmpty: 'Vælg en celle for at se, hvordan dens værdi er lavet.',
    fromSources: 'Fra {count} kilder',
    fromSource: 'Fra 1 kilde',
    typedValue: 'Indtastet værdi',
    errorCycle: 'Cirkulær reference',
    errorUnsupported: 'Understøttes ikke',
    errorMissingInput: 'Et input mangler',
    errorDivisionByZero: 'Division med nul',
    errorTypeError: 'Forkert slags værdi',
    errorGeneric: 'Fejl',
    runtimeOpening: 'Åbner…',
    runtimeInvalidated: 'Denne visning blev lukket.',
    runtimeFailed: 'Denne visning kunne ikke åbnes.',
    runtimeReload: 'Åbn den igen',
    sourcesTitle: 'Kilder',
    sourcesValue: 'Værdi',
    sourcesSource: 'Kilde',
    sourcesAsOf: 'Pr.',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Detaljer',
  },
  // German
  de: {
    valueMissing: 'Nicht verfügbar',
    valueRedacted: 'Ausgeblendet',
    valueSourceNull: 'Kein Wert in der Quelle',
    valuePending: 'Wird noch geschrieben',
    tablePlaceholder: 'Tabelle',
    tableDescribed: 'Tabelle: {title}',
    tableRows: 'Zeilen: {count}',
    openInSheet: 'Im Tabellenblatt öffnen',
    chartPlaceholder: 'Diagramm',
    chartDescribed: 'Diagramm: {title}',
    openChart: 'Öffnen',
    imageUnavailable: 'Bild nicht verfügbar',
    pageLabel: 'Seite {at} von {count}',
    documentLabel: 'Dokument',
    reportLabel: 'Bericht',
    sheetTabs: 'Tabellenblätter',
    sheetGrid: 'Tabellenblatt {title}',
    formulaBar: 'Formel',
    formulaBarEmpty: 'Wählen Sie eine Zelle aus, um zu sehen, wie ihr Wert berechnet wird.',
    fromSources: 'Aus {count} Quellen',
    fromSource: 'Aus 1 Quelle',
    typedValue: 'Eingegebener Wert',
    errorCycle: 'Zirkelbezug',
    errorUnsupported: 'Nicht unterstützt',
    errorMissingInput: 'Eine Eingabe fehlt',
    errorDivisionByZero: 'Division durch null',
    errorTypeError: 'Falsche Art von Wert',
    errorGeneric: 'Fehler',
    runtimeOpening: 'Wird geöffnet…',
    runtimeInvalidated: 'Diese Ansicht wurde geschlossen.',
    runtimeFailed: 'Diese Ansicht konnte nicht geöffnet werden.',
    runtimeReload: 'Erneut öffnen',
    sourcesTitle: 'Quellen',
    sourcesValue: 'Wert',
    sourcesSource: 'Quelle',
    sourcesAsOf: 'Stand',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Details',
  },
  // Greek
  el: {
    valueMissing: 'Μη διαθέσιμο',
    valueRedacted: 'Κρυφό',
    valueSourceNull: 'Δεν υπάρχει τιμή στην πηγή',
    valuePending: 'Γράφεται ακόμη',
    tablePlaceholder: 'Πίνακας',
    tableDescribed: 'Πίνακας: {title}',
    tableRows: 'Γραμμές: {count}',
    openInSheet: 'Άνοιγμα στο φύλλο',
    chartPlaceholder: 'Γράφημα',
    chartDescribed: 'Γράφημα: {title}',
    openChart: 'Άνοιγμα',
    imageUnavailable: 'Η εικόνα δεν είναι διαθέσιμη',
    pageLabel: 'Σελίδα {at} από {count}',
    documentLabel: 'Έγγραφο',
    reportLabel: 'Αναφορά',
    sheetTabs: 'Φύλλα',
    sheetGrid: 'Φύλλο {title}',
    formulaBar: 'Τύπος',
    formulaBarEmpty: 'Επιλέξτε ένα κελί για να δείτε πώς προκύπτει η τιμή του.',
    fromSources: 'Από {count} πηγές',
    fromSource: 'Από 1 πηγή',
    typedValue: 'Τιμή που καταχωρίστηκε',
    errorCycle: 'Κυκλική αναφορά',
    errorUnsupported: 'Δεν υποστηρίζεται',
    errorMissingInput: 'Λείπει μια είσοδος',
    errorDivisionByZero: 'Διαίρεση με το μηδέν',
    errorTypeError: 'Λάθος είδος τιμής',
    errorGeneric: 'Σφάλμα',
    runtimeOpening: 'Άνοιγμα…',
    runtimeInvalidated: 'Αυτή η προβολή έκλεισε.',
    runtimeFailed: 'Αυτή η προβολή δεν μπόρεσε να ανοίξει.',
    runtimeReload: 'Άνοιγμα ξανά',
    sourcesTitle: 'Πηγές',
    sourcesValue: 'Τιμή',
    sourcesSource: 'Πηγή',
    sourcesAsOf: 'Κατά τις',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Λεπτομέρειες',
  },
  // Spanish
  es: {
    valueMissing: 'No disponible',
    valueRedacted: 'Oculto',
    valueSourceNull: 'Sin valor en la fuente',
    valuePending: 'Aún se está escribiendo',
    tablePlaceholder: 'Tabla',
    tableDescribed: 'Tabla: {title}',
    tableRows: 'Filas: {count}',
    openInSheet: 'Abrir en la hoja',
    chartPlaceholder: 'Gráfico',
    chartDescribed: 'Gráfico: {title}',
    openChart: 'Abrir',
    imageUnavailable: 'Imagen no disponible',
    pageLabel: 'Página {at} de {count}',
    documentLabel: 'Documento',
    reportLabel: 'Informe',
    sheetTabs: 'Hojas',
    sheetGrid: 'Hoja {title}',
    formulaBar: 'Fórmula',
    formulaBarEmpty: 'Selecciona una celda para ver cómo se obtiene su valor.',
    fromSources: 'De {count} fuentes',
    fromSource: 'De 1 fuente',
    typedValue: 'Valor introducido',
    errorCycle: 'Referencia circular',
    errorUnsupported: 'No compatible',
    errorMissingInput: 'Falta una entrada',
    errorDivisionByZero: 'División por cero',
    errorTypeError: 'Tipo de valor incorrecto',
    errorGeneric: 'Error',
    runtimeOpening: 'Abriendo…',
    runtimeInvalidated: 'Esta vista se cerró.',
    runtimeFailed: 'No se pudo abrir esta vista.',
    runtimeReload: 'Abrir de nuevo',
    sourcesTitle: 'Fuentes',
    sourcesValue: 'Valor',
    sourcesSource: 'Fuente',
    sourcesAsOf: 'A fecha de',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Detalles',
  },
  // Estonian
  et: {
    valueMissing: 'Pole saadaval',
    valueRedacted: 'Peidetud',
    valueSourceNull: 'Allikas pole väärtust',
    valuePending: 'Veel kirjutatakse',
    tablePlaceholder: 'Tabel',
    tableDescribed: 'Tabel: {title}',
    tableRows: 'Ridu: {count}',
    openInSheet: 'Ava lehel',
    chartPlaceholder: 'Diagramm',
    chartDescribed: 'Diagramm: {title}',
    openChart: 'Ava',
    imageUnavailable: 'Pilt pole saadaval',
    pageLabel: 'Leht {at} / {count}',
    documentLabel: 'Dokument',
    reportLabel: 'Aruanne',
    sheetTabs: 'Lehed',
    sheetGrid: 'Leht {title}',
    formulaBar: 'Valem',
    formulaBarEmpty: 'Vali lahter, et näha, kuidas selle väärtus tekib.',
    fromSources: '{count} allikast',
    fromSource: '1 allikast',
    typedValue: 'Sisestatud väärtus',
    errorCycle: 'Tsükliline viide',
    errorUnsupported: 'Ei toetata',
    errorMissingInput: 'Sisend puudub',
    errorDivisionByZero: 'Nulliga jagamine',
    errorTypeError: 'Vale liiki väärtus',
    errorGeneric: 'Viga',
    runtimeOpening: 'Avatakse…',
    runtimeInvalidated: 'See vaade suleti.',
    runtimeFailed: 'Seda vaadet ei õnnestunud avada.',
    runtimeReload: 'Ava uuesti',
    sourcesTitle: 'Allikad',
    sourcesValue: 'Väärtus',
    sourcesSource: 'Allikas',
    sourcesAsOf: 'Seisuga',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Üksikasjad',
  },
  // Finnish
  fi: {
    valueMissing: 'Ei saatavilla',
    valueRedacted: 'Piilotettu',
    valueSourceNull: 'Lähteessä ei ole arvoa',
    valuePending: 'Kirjoitetaan vielä',
    tablePlaceholder: 'Taulukko',
    tableDescribed: 'Taulukko: {title}',
    tableRows: 'Rivejä: {count}',
    openInSheet: 'Avaa laskentataulukossa',
    chartPlaceholder: 'Kaavio',
    chartDescribed: 'Kaavio: {title}',
    openChart: 'Avaa',
    imageUnavailable: 'Kuva ei ole saatavilla',
    pageLabel: 'Sivu {at} / {count}',
    documentLabel: 'Asiakirja',
    reportLabel: 'Raportti',
    sheetTabs: 'Taulukkovälilehdet',
    sheetGrid: 'Taulukko {title}',
    formulaBar: 'Kaava',
    formulaBarEmpty: 'Valitse solu nähdäksesi, miten sen arvo muodostuu.',
    fromSources: '{count} lähteestä',
    fromSource: '1 lähteestä',
    typedValue: 'Syötetty arvo',
    errorCycle: 'Kehäviittaus',
    errorUnsupported: 'Ei tuettu',
    errorMissingInput: 'Syöte puuttuu',
    errorDivisionByZero: 'Nollalla jakaminen',
    errorTypeError: 'Väärän tyyppinen arvo',
    errorGeneric: 'Virhe',
    runtimeOpening: 'Avataan…',
    runtimeInvalidated: 'Tämä näkymä suljettiin.',
    runtimeFailed: 'Tätä näkymää ei voitu avata.',
    runtimeReload: 'Avaa uudelleen',
    sourcesTitle: 'Lähteet',
    sourcesValue: 'Arvo',
    sourcesSource: 'Lähde',
    sourcesAsOf: 'Tilanne',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Lisätiedot',
  },
  // French
  fr: {
    valueMissing: 'Non disponible',
    valueRedacted: 'Masqué',
    valueSourceNull: 'Aucune valeur dans la source',
    valuePending: 'Encore en cours de rédaction',
    tablePlaceholder: 'Tableau',
    tableDescribed: 'Tableau : {title}',
    tableRows: 'Lignes : {count}',
    openInSheet: 'Ouvrir dans la feuille',
    chartPlaceholder: 'Graphique',
    chartDescribed: 'Graphique : {title}',
    openChart: 'Ouvrir',
    imageUnavailable: 'Image non disponible',
    pageLabel: 'Page {at} sur {count}',
    documentLabel: 'Document',
    reportLabel: 'Rapport',
    sheetTabs: 'Feuilles',
    sheetGrid: 'Feuille {title}',
    formulaBar: 'Formule',
    formulaBarEmpty: 'Sélectionnez une cellule pour voir comment sa valeur est obtenue.',
    fromSources: 'Issu de {count} sources',
    fromSource: 'Issu d’1 source',
    typedValue: 'Valeur saisie',
    errorCycle: 'Référence circulaire',
    errorUnsupported: 'Non pris en charge',
    errorMissingInput: 'Une entrée est manquante',
    errorDivisionByZero: 'Division par zéro',
    errorTypeError: 'Type de valeur incorrect',
    errorGeneric: 'Erreur',
    runtimeOpening: 'Ouverture…',
    runtimeInvalidated: 'Cette vue a été fermée.',
    runtimeFailed: 'Cette vue n’a pas pu être ouverte.',
    runtimeReload: 'La rouvrir',
    sourcesTitle: 'Sources',
    sourcesValue: 'Valeur',
    sourcesSource: 'Source',
    sourcesAsOf: 'Au',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Détails',
  },
  // Irish
  ga: {
    valueMissing: 'Níl ar fáil',
    valueRedacted: 'Folaithe',
    valueSourceNull: 'Níl aon luach sa fhoinse',
    valuePending: 'Á scríobh fós',
    tablePlaceholder: 'Tábla',
    tableDescribed: 'Tábla: {title}',
    tableRows: 'Sraitheanna: {count}',
    openInSheet: 'Oscail sa bhileog',
    chartPlaceholder: 'Cairt',
    chartDescribed: 'Cairt: {title}',
    openChart: 'Oscail',
    imageUnavailable: 'Níl an íomhá ar fáil',
    pageLabel: 'Leathanach {at} as {count}',
    documentLabel: 'Cáipéis',
    reportLabel: 'Tuarascáil',
    sheetTabs: 'Bileoga',
    sheetGrid: 'Bileog {title}',
    formulaBar: 'Foirmle',
    formulaBarEmpty: 'Roghnaigh cill chun a fheiceáil conas a fhaightear a luach.',
    fromSources: 'Ó {count} fhoinse',
    fromSource: 'Ó fhoinse amháin',
    typedValue: 'Luach a iontráladh',
    errorCycle: 'Tagairt chiorclach',
    errorUnsupported: 'Gan tacaíocht',
    errorMissingInput: 'Tá ionchur ar iarraidh',
    errorDivisionByZero: 'Roinnt ar nialas',
    errorTypeError: 'Cineál luacha mícheart',
    errorGeneric: 'Earráid',
    runtimeOpening: 'Á oscailt…',
    runtimeInvalidated: 'Dúnadh an radharc seo.',
    runtimeFailed: 'Níorbh fhéidir an radharc seo a oscailt.',
    runtimeReload: 'Oscail arís é',
    sourcesTitle: 'Foinsí',
    sourcesValue: 'Luach',
    sourcesSource: 'Foinse',
    sourcesAsOf: 'Amhail',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Sonraí',
  },
  // Croatian
  hr: {
    valueMissing: 'Nije dostupno',
    valueRedacted: 'Skriveno',
    valueSourceNull: 'U izvoru nema vrijednosti',
    valuePending: 'Još se piše',
    tablePlaceholder: 'Tablica',
    tableDescribed: 'Tablica: {title}',
    tableRows: 'Redaka: {count}',
    openInSheet: 'Otvori u listu',
    chartPlaceholder: 'Grafikon',
    chartDescribed: 'Grafikon: {title}',
    openChart: 'Otvori',
    imageUnavailable: 'Slika nije dostupna',
    pageLabel: 'Stranica {at} od {count}',
    documentLabel: 'Dokument',
    reportLabel: 'Izvješće',
    sheetTabs: 'Listovi',
    sheetGrid: 'List {title}',
    formulaBar: 'Formula',
    formulaBarEmpty: 'Odaberite ćeliju da vidite kako je nastala njezina vrijednost.',
    fromSources: 'Iz {count} izvora',
    fromSource: 'Iz 1 izvora',
    typedValue: 'Unesena vrijednost',
    errorCycle: 'Kružna referenca',
    errorUnsupported: 'Nije podržano',
    errorMissingInput: 'Nedostaje ulaz',
    errorDivisionByZero: 'Dijeljenje nulom',
    errorTypeError: 'Pogrešna vrsta vrijednosti',
    errorGeneric: 'Pogreška',
    runtimeOpening: 'Otvaranje…',
    runtimeInvalidated: 'Ovaj prikaz je zatvoren.',
    runtimeFailed: 'Ovaj prikaz nije bilo moguće otvoriti.',
    runtimeReload: 'Otvori ponovno',
    sourcesTitle: 'Izvori',
    sourcesValue: 'Vrijednost',
    sourcesSource: 'Izvor',
    sourcesAsOf: 'Na dan',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Pojedinosti',
  },
  // Hungarian
  hu: {
    valueMissing: 'Nem érhető el',
    valueRedacted: 'Elrejtve',
    valueSourceNull: 'Nincs érték a forrásban',
    valuePending: 'Még íródik',
    tablePlaceholder: 'Táblázat',
    tableDescribed: 'Táblázat: {title}',
    tableRows: 'Sorok: {count}',
    openInSheet: 'Megnyitás a munkalapon',
    chartPlaceholder: 'Diagram',
    chartDescribed: 'Diagram: {title}',
    openChart: 'Megnyitás',
    imageUnavailable: 'A kép nem érhető el',
    pageLabel: '{at}. oldal / {count}',
    documentLabel: 'Dokumentum',
    reportLabel: 'Jelentés',
    sheetTabs: 'Munkalapok',
    sheetGrid: '{title} munkalap',
    formulaBar: 'Képlet',
    formulaBarEmpty: 'Válassz egy cellát, hogy lásd, hogyan áll elő az értéke.',
    fromSources: '{count} forrásból',
    fromSource: '1 forrásból',
    typedValue: 'Beírt érték',
    errorCycle: 'Körkörös hivatkozás',
    errorUnsupported: 'Nem támogatott',
    errorMissingInput: 'Hiányzik egy bemenet',
    errorDivisionByZero: 'Nullával osztás',
    errorTypeError: 'Rossz típusú érték',
    errorGeneric: 'Hiba',
    runtimeOpening: 'Megnyitás…',
    runtimeInvalidated: 'Ezt a nézetet bezárták.',
    runtimeFailed: 'Ezt a nézetet nem sikerült megnyitni.',
    runtimeReload: 'Megnyitás újra',
    sourcesTitle: 'Források',
    sourcesValue: 'Érték',
    sourcesSource: 'Forrás',
    sourcesAsOf: 'Állapot:',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Részletek',
  },
  // Italian
  it: {
    valueMissing: 'Non disponibile',
    valueRedacted: 'Nascosto',
    valueSourceNull: 'Nessun valore nella fonte',
    valuePending: 'Ancora in scrittura',
    tablePlaceholder: 'Tabella',
    tableDescribed: 'Tabella: {title}',
    tableRows: 'Righe: {count}',
    openInSheet: 'Apri nel foglio',
    chartPlaceholder: 'Grafico',
    chartDescribed: 'Grafico: {title}',
    openChart: 'Apri',
    imageUnavailable: 'Immagine non disponibile',
    pageLabel: 'Pagina {at} di {count}',
    documentLabel: 'Documento',
    reportLabel: 'Report',
    sheetTabs: 'Fogli',
    sheetGrid: 'Foglio {title}',
    formulaBar: 'Formula',
    formulaBarEmpty: 'Seleziona una cella per vedere come si ottiene il suo valore.',
    fromSources: 'Da {count} fonti',
    fromSource: 'Da 1 fonte',
    typedValue: 'Valore inserito',
    errorCycle: 'Riferimento circolare',
    errorUnsupported: 'Non supportato',
    errorMissingInput: 'Manca un input',
    errorDivisionByZero: 'Divisione per zero',
    errorTypeError: 'Tipo di valore errato',
    errorGeneric: 'Errore',
    runtimeOpening: 'Apertura…',
    runtimeInvalidated: 'Questa vista è stata chiusa.',
    runtimeFailed: 'Non è stato possibile aprire questa vista.',
    runtimeReload: 'Riaprila',
    sourcesTitle: 'Fonti',
    sourcesValue: 'Valore',
    sourcesSource: 'Fonte',
    sourcesAsOf: 'Al',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Dettagli',
  },
  // Lithuanian
  lt: {
    valueMissing: 'Nepasiekiama',
    valueRedacted: 'Paslėpta',
    valueSourceNull: 'Šaltinyje nėra reikšmės',
    valuePending: 'Dar rašoma',
    tablePlaceholder: 'Lentelė',
    tableDescribed: 'Lentelė: {title}',
    tableRows: 'Eilučių: {count}',
    openInSheet: 'Atidaryti lape',
    chartPlaceholder: 'Diagrama',
    chartDescribed: 'Diagrama: {title}',
    openChart: 'Atidaryti',
    imageUnavailable: 'Vaizdas nepasiekiamas',
    pageLabel: '{at} puslapis iš {count}',
    documentLabel: 'Dokumentas',
    reportLabel: 'Ataskaita',
    sheetTabs: 'Lapai',
    sheetGrid: 'Lapas {title}',
    formulaBar: 'Formulė',
    formulaBarEmpty: 'Pasirinkite langelį, kad pamatytumėte, kaip gaunama jo reikšmė.',
    fromSources: 'Iš {count} šaltinių',
    fromSource: 'Iš 1 šaltinio',
    typedValue: 'Įvesta reikšmė',
    errorCycle: 'Ciklinė nuoroda',
    errorUnsupported: 'Nepalaikoma',
    errorMissingInput: 'Trūksta įvesties',
    errorDivisionByZero: 'Dalyba iš nulio',
    errorTypeError: 'Netinkamo tipo reikšmė',
    errorGeneric: 'Klaida',
    runtimeOpening: 'Atidaroma…',
    runtimeInvalidated: 'Šis rodinys buvo uždarytas.',
    runtimeFailed: 'Šio rodinio nepavyko atidaryti.',
    runtimeReload: 'Atidaryti dar kartą',
    sourcesTitle: 'Šaltiniai',
    sourcesValue: 'Reikšmė',
    sourcesSource: 'Šaltinis',
    sourcesAsOf: 'Pagal',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Išsami informacija',
  },
  // Latvian
  lv: {
    valueMissing: 'Nav pieejams',
    valueRedacted: 'Paslēpts',
    valueSourceNull: 'Avotā nav vērtības',
    valuePending: 'Vēl tiek rakstīts',
    tablePlaceholder: 'Tabula',
    tableDescribed: 'Tabula: {title}',
    tableRows: 'Rindas: {count}',
    openInSheet: 'Atvērt lapā',
    chartPlaceholder: 'Diagramma',
    chartDescribed: 'Diagramma: {title}',
    openChart: 'Atvērt',
    imageUnavailable: 'Attēls nav pieejams',
    pageLabel: '{at}. lappuse no {count}',
    documentLabel: 'Dokuments',
    reportLabel: 'Pārskats',
    sheetTabs: 'Lapas',
    sheetGrid: 'Lapa {title}',
    formulaBar: 'Formula',
    formulaBarEmpty: 'Atlasiet šūnu, lai redzētu, kā veidojas tās vērtība.',
    fromSources: 'No {count} avotiem',
    fromSource: 'No 1 avota',
    typedValue: 'Ievadītā vērtība',
    errorCycle: 'Cikliska atsauce',
    errorUnsupported: 'Netiek atbalstīts',
    errorMissingInput: 'Trūkst ievades',
    errorDivisionByZero: 'Dalīšana ar nulli',
    errorTypeError: 'Nepareizs vērtības veids',
    errorGeneric: 'Kļūda',
    runtimeOpening: 'Atver…',
    runtimeInvalidated: 'Šis skats tika aizvērts.',
    runtimeFailed: 'Šo skatu neizdevās atvērt.',
    runtimeReload: 'Atvērt vēlreiz',
    sourcesTitle: 'Avoti',
    sourcesValue: 'Vērtība',
    sourcesSource: 'Avots',
    sourcesAsOf: 'Uz',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Detalizēta informācija',
  },
  // Maltese
  mt: {
    valueMissing: 'Mhux disponibbli',
    valueRedacted: 'Moħbi',
    valueSourceNull: 'M’hemm l-ebda valur fis-sors',
    valuePending: 'Għadu qed jinkiteb',
    tablePlaceholder: 'Tabella',
    tableDescribed: 'Tabella: {title}',
    tableRows: 'Ringieli: {count}',
    openInSheet: 'Iftaħ fil-folja',
    chartPlaceholder: 'Grafika',
    chartDescribed: 'Grafika: {title}',
    openChart: 'Iftaħ',
    imageUnavailable: 'L-immaġni mhijiex disponibbli',
    pageLabel: 'Paġna {at} minn {count}',
    documentLabel: 'Dokument',
    reportLabel: 'Rapport',
    sheetTabs: 'Folji',
    sheetGrid: 'Folja {title}',
    formulaBar: 'Formula',
    formulaBarEmpty: 'Agħżel ċellola biex tara kif inkiseb il-valur tagħha.',
    fromSources: 'Minn {count} sorsi',
    fromSource: 'Minn sors 1',
    typedValue: 'Valur imdaħħal',
    errorCycle: 'Referenza ċirkolari',
    errorUnsupported: 'Mhux appoġġjat',
    errorMissingInput: 'Input nieqes',
    errorDivisionByZero: 'Diviżjoni b’żero',
    errorTypeError: 'Tip ta’ valur ħażin',
    errorGeneric: 'Żball',
    runtimeOpening: 'Qed jinfetaħ…',
    runtimeInvalidated: 'Din il-veduta ngħalqet.',
    runtimeFailed: 'Din il-veduta ma setgħetx tinfetaħ.',
    runtimeReload: 'Iftaħha mill-ġdid',
    sourcesTitle: 'Sorsi',
    sourcesValue: 'Valur',
    sourcesSource: 'Sors',
    sourcesAsOf: 'Sa',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Dettalji',
  },
  // Dutch
  nl: {
    valueMissing: 'Niet beschikbaar',
    valueRedacted: 'Verborgen',
    valueSourceNull: 'Geen waarde in de bron',
    valuePending: 'Wordt nog geschreven',
    tablePlaceholder: 'Tabel',
    tableDescribed: 'Tabel: {title}',
    tableRows: 'Rijen: {count}',
    openInSheet: 'Openen in het blad',
    chartPlaceholder: 'Grafiek',
    chartDescribed: 'Grafiek: {title}',
    openChart: 'Openen',
    imageUnavailable: 'Afbeelding niet beschikbaar',
    pageLabel: 'Pagina {at} van {count}',
    documentLabel: 'Document',
    reportLabel: 'Rapport',
    sheetTabs: 'Bladen',
    sheetGrid: 'Blad {title}',
    formulaBar: 'Formule',
    formulaBarEmpty: 'Selecteer een cel om te zien hoe de waarde tot stand komt.',
    fromSources: 'Uit {count} bronnen',
    fromSource: 'Uit 1 bron',
    typedValue: 'Ingevoerde waarde',
    errorCycle: 'Circulaire verwijzing',
    errorUnsupported: 'Niet ondersteund',
    errorMissingInput: 'Er ontbreekt een invoer',
    errorDivisionByZero: 'Deling door nul',
    errorTypeError: 'Verkeerd soort waarde',
    errorGeneric: 'Fout',
    runtimeOpening: 'Openen…',
    runtimeInvalidated: 'Deze weergave is gesloten.',
    runtimeFailed: 'Deze weergave kon niet worden geopend.',
    runtimeReload: 'Opnieuw openen',
    sourcesTitle: 'Bronnen',
    sourcesValue: 'Waarde',
    sourcesSource: 'Bron',
    sourcesAsOf: 'Per',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Details',
  },
  // Polish
  pl: {
    valueMissing: 'Niedostępne',
    valueRedacted: 'Ukryte',
    valueSourceNull: 'Brak wartości w źródle',
    valuePending: 'Wciąż jest pisane',
    tablePlaceholder: 'Tabela',
    tableDescribed: 'Tabela: {title}',
    tableRows: 'Wiersze: {count}',
    openInSheet: 'Otwórz w arkuszu',
    chartPlaceholder: 'Wykres',
    chartDescribed: 'Wykres: {title}',
    openChart: 'Otwórz',
    imageUnavailable: 'Obraz niedostępny',
    pageLabel: 'Strona {at} z {count}',
    documentLabel: 'Dokument',
    reportLabel: 'Raport',
    sheetTabs: 'Arkusze',
    sheetGrid: 'Arkusz {title}',
    formulaBar: 'Formuła',
    formulaBarEmpty: 'Zaznacz komórkę, aby zobaczyć, jak powstała jej wartość.',
    fromSources: 'Z {count} źródeł',
    fromSource: 'Z 1 źródła',
    typedValue: 'Wprowadzona wartość',
    errorCycle: 'Odwołanie cykliczne',
    errorUnsupported: 'Nieobsługiwane',
    errorMissingInput: 'Brakuje danych wejściowych',
    errorDivisionByZero: 'Dzielenie przez zero',
    errorTypeError: 'Nieprawidłowy rodzaj wartości',
    errorGeneric: 'Błąd',
    runtimeOpening: 'Otwieranie…',
    runtimeInvalidated: 'Ten widok został zamknięty.',
    runtimeFailed: 'Nie udało się otworzyć tego widoku.',
    runtimeReload: 'Otwórz ponownie',
    sourcesTitle: 'Źródła',
    sourcesValue: 'Wartość',
    sourcesSource: 'Źródło',
    sourcesAsOf: 'Stan na',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Szczegóły',
  },
  // Portuguese
  pt: {
    valueMissing: 'Não disponível',
    valueRedacted: 'Oculto',
    valueSourceNull: 'Sem valor na fonte',
    valuePending: 'Ainda a ser escrito',
    tablePlaceholder: 'Tabela',
    tableDescribed: 'Tabela: {title}',
    tableRows: 'Linhas: {count}',
    openInSheet: 'Abrir na folha',
    chartPlaceholder: 'Gráfico',
    chartDescribed: 'Gráfico: {title}',
    openChart: 'Abrir',
    imageUnavailable: 'Imagem não disponível',
    pageLabel: 'Página {at} de {count}',
    documentLabel: 'Documento',
    reportLabel: 'Relatório',
    sheetTabs: 'Folhas',
    sheetGrid: 'Folha {title}',
    formulaBar: 'Fórmula',
    formulaBarEmpty: 'Seleciona uma célula para ver como o seu valor é obtido.',
    fromSources: 'De {count} fontes',
    fromSource: 'De 1 fonte',
    typedValue: 'Valor introduzido',
    errorCycle: 'Referência circular',
    errorUnsupported: 'Não suportado',
    errorMissingInput: 'Falta uma entrada',
    errorDivisionByZero: 'Divisão por zero',
    errorTypeError: 'Tipo de valor incorreto',
    errorGeneric: 'Erro',
    runtimeOpening: 'A abrir…',
    runtimeInvalidated: 'Esta vista foi fechada.',
    runtimeFailed: 'Não foi possível abrir esta vista.',
    runtimeReload: 'Abrir novamente',
    sourcesTitle: 'Fontes',
    sourcesValue: 'Valor',
    sourcesSource: 'Fonte',
    sourcesAsOf: 'A',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Detalhes',
  },
  // Romanian
  ro: {
    valueMissing: 'Indisponibil',
    valueRedacted: 'Ascuns',
    valueSourceNull: 'Nicio valoare în sursă',
    valuePending: 'Încă se scrie',
    tablePlaceholder: 'Tabel',
    tableDescribed: 'Tabel: {title}',
    tableRows: 'Rânduri: {count}',
    openInSheet: 'Deschide în foaie',
    chartPlaceholder: 'Grafic',
    chartDescribed: 'Grafic: {title}',
    openChart: 'Deschide',
    imageUnavailable: 'Imaginea nu este disponibilă',
    pageLabel: 'Pagina {at} din {count}',
    documentLabel: 'Document',
    reportLabel: 'Raport',
    sheetTabs: 'Foi',
    sheetGrid: 'Foaia {title}',
    formulaBar: 'Formulă',
    formulaBarEmpty: 'Selectează o celulă ca să vezi cum este obținută valoarea ei.',
    fromSources: 'Din {count} surse',
    fromSource: 'Dintr-o sursă',
    typedValue: 'Valoare introdusă',
    errorCycle: 'Referință circulară',
    errorUnsupported: 'Neacceptat',
    errorMissingInput: 'Lipsește o intrare',
    errorDivisionByZero: 'Împărțire la zero',
    errorTypeError: 'Tip de valoare greșit',
    errorGeneric: 'Eroare',
    runtimeOpening: 'Se deschide…',
    runtimeInvalidated: 'Această vizualizare a fost închisă.',
    runtimeFailed: 'Această vizualizare nu a putut fi deschisă.',
    runtimeReload: 'Deschide din nou',
    sourcesTitle: 'Surse',
    sourcesValue: 'Valoare',
    sourcesSource: 'Sursă',
    sourcesAsOf: 'La data de',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Detalii',
  },
  // Slovak
  sk: {
    valueMissing: 'Nie je k dispozícii',
    valueRedacted: 'Skryté',
    valueSourceNull: 'V zdroji nie je hodnota',
    valuePending: 'Stále sa píše',
    tablePlaceholder: 'Tabuľka',
    tableDescribed: 'Tabuľka: {title}',
    tableRows: 'Riadkov: {count}',
    openInSheet: 'Otvoriť v hárku',
    chartPlaceholder: 'Graf',
    chartDescribed: 'Graf: {title}',
    openChart: 'Otvoriť',
    imageUnavailable: 'Obrázok nie je k dispozícii',
    pageLabel: 'Strana {at} z {count}',
    documentLabel: 'Dokument',
    reportLabel: 'Správa',
    sheetTabs: 'Hárky',
    sheetGrid: 'Hárok {title}',
    formulaBar: 'Vzorec',
    formulaBarEmpty: 'Vyberte bunku a uvidíte, ako vznikla jej hodnota.',
    fromSources: 'Z {count} zdrojov',
    fromSource: 'Z 1 zdroja',
    typedValue: 'Zadaná hodnota',
    errorCycle: 'Cyklický odkaz',
    errorUnsupported: 'Nepodporované',
    errorMissingInput: 'Chýba vstup',
    errorDivisionByZero: 'Delenie nulou',
    errorTypeError: 'Nesprávny druh hodnoty',
    errorGeneric: 'Chyba',
    runtimeOpening: 'Otvára sa…',
    runtimeInvalidated: 'Toto zobrazenie bolo zatvorené.',
    runtimeFailed: 'Toto zobrazenie sa nepodarilo otvoriť.',
    runtimeReload: 'Otvoriť znova',
    sourcesTitle: 'Zdroje',
    sourcesValue: 'Hodnota',
    sourcesSource: 'Zdroj',
    sourcesAsOf: 'K dátumu',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Podrobnosti',
  },
  // Slovenian
  sl: {
    valueMissing: 'Ni na voljo',
    valueRedacted: 'Skrito',
    valueSourceNull: 'V viru ni vrednosti',
    valuePending: 'Še se piše',
    tablePlaceholder: 'Tabela',
    tableDescribed: 'Tabela: {title}',
    tableRows: 'Vrstic: {count}',
    openInSheet: 'Odpri v preglednici',
    chartPlaceholder: 'Grafikon',
    chartDescribed: 'Grafikon: {title}',
    openChart: 'Odpri',
    imageUnavailable: 'Slika ni na voljo',
    pageLabel: 'Stran {at} od {count}',
    documentLabel: 'Dokument',
    reportLabel: 'Poročilo',
    sheetTabs: 'Listi',
    sheetGrid: 'List {title}',
    formulaBar: 'Formula',
    formulaBarEmpty: 'Izberite celico, da vidite, kako je nastala njena vrednost.',
    fromSources: 'Iz {count} virov',
    fromSource: 'Iz 1 vira',
    typedValue: 'Vnesena vrednost',
    errorCycle: 'Krožna referenca',
    errorUnsupported: 'Ni podprto',
    errorMissingInput: 'Manjka vnos',
    errorDivisionByZero: 'Deljenje z ničlo',
    errorTypeError: 'Napačna vrsta vrednosti',
    errorGeneric: 'Napaka',
    runtimeOpening: 'Odpiranje …',
    runtimeInvalidated: 'Ta pogled je bil zaprt.',
    runtimeFailed: 'Tega pogleda ni bilo mogoče odpreti.',
    runtimeReload: 'Odpri znova',
    sourcesTitle: 'Viri',
    sourcesValue: 'Vrednost',
    sourcesSource: 'Vir',
    sourcesAsOf: 'Stanje na dan',
    slideNumber: '{at} / {count}',
    runtimeDetails: 'Podrobnosti',
  },
};

const artifactContentCopy: NamespaceCatalog<Key> = { en, sv, ...translated };

export default artifactContentCopy;
